import React, { useMemo, useState } from "react";
import styled from "styled-components";

import Modal from "../modal/Modal";
import ComboInput from "../form-item/ComboInput";
import Input from "../form-item/Input";
import Textarea from "../form-item/Textarea";
import CompensationLinesEditor from "./CompensationLinesEditor";
import DateInput, { type DateInputValue } from "../calendar/DateInput";
import {
  Accordion,
  AccordionBody,
  AccordionSummary,
  BalanceNoticeWarning,
  FieldLabel,
  FieldRow,
  FormRoot,
  FormSection,
  FormSectionTitle,
  InlineRow,
} from "./OfferFormLayout";
import {
  isSubcontractOfferForm,
  useGetDmThreadDetailQuery,
  type DmThread,
  type ProposeContractAmendmentParams,
  type SubcontractOfferForm,
} from "../../app/services/dmThreads";
// @ts-ignore - JS コンポーネント（型定義なし）
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import { evaluateOfferEscrowBalanceCheck } from "../../utils/balance";
import { formatYen } from "../../utils/money";
import {
  computeCompensationSubtotalExclusive,
  mapCompensationLinesToApi,
} from "../../utils/offerCompensationLines";
import { addConsumptionTax } from "../../utils/tax";
import type { OfferCompensationLine } from "./types";

interface ContractAmendModalProps {
  thread: DmThread;
  projectId: string;
  onClose: () => void;
  onSubmit: (changes: ContractAmendmentDraft) => void;
  isSubmitting?: boolean;
  errorMessage?: string | null;
}

/** `proposeContractAmendment` に渡す差分（project_id / dm_id は呼び出し側が付与する）。 */
export type ContractAmendmentDraft = Omit<
  ProposeContractAmendmentParams,
  "project_id" | "dm_id"
>;

const Notice = styled.div.attrs({ className: "bodytext-13" })`
  padding: 12px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  background: #fff;
`;

const ErrorText = styled.div.attrs({ className: "bodytext-13" })`
  color: var(--soremo-green);
`;

/**
 * 編集状態。API へ送るのは初期値から実際に変わったフィールドだけ。
 * 項目は `steps/DetailFormStep` の契約フォームと 1:1 に対応させる。
 */
interface AmendFormState {
  assignedRoles: string;
  deliverableFormat: string;
  deadline: string | null;
  compensationLines: OfferCompensationLine[];
  specialProvisions: string;
  scope: string;
  initialMessage: string;
  contractStartDate: string | null;
  contractEndDate: string | null;
  deliveryMethod: string;
  deliveryLocation: string;
}

const toDate = (iso: string | null): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** `DateInput`（single モード）の値を ISO 文字列に戻す。 */
const toIso = (value: DateInputValue): string | null => {
  const date = Array.isArray(value) ? value[0] : value;
  return date ? date.toISOString() : null;
};

const sameLines = (
  a: OfferCompensationLine[],
  b: OfferCompensationLine[],
): boolean => {
  if (a.length !== b.length) return false;
  return a.every((line, i) => {
    const other = b[i];
    return (
      line.label === other.label &&
      line.unitPrice === other.unitPrice &&
      line.quantity === other.quantity &&
      line.unit === other.unit
    );
  });
};

/**
 * 締結後の契約更新を提案するモーダル（委託者のみ）。
 *
 * 入力項目・セクション構成・スタイルは**オファー作成ウィザードの契約フォーム**
 * （`steps/DetailFormStep`）と揃える。「契約書を作るとき」と「契約書を更新する
 * とき」で別の画面に見えないようにするため、枠組みは `OfferFormLayout` を、
 * 対価テーブルは `CompensationLinesEditor` を共有している。
 *
 * 締結後に変更できないフィールド（再委託可否・メッセージ・有効期限など、状態機械
 * や当事者を規定するもの）はウィザードにあっても出さない。
 *
 * **送信しても契約は変わらない。** 受託者が承認して初めて反映される。条件の悪化
 * （減額・納期の前倒し・スコープ拡大）は入力上も制限しない。担保は承認ゲートと、
 * タイムラインに残る差分 + 更新前の契約書 PDF で取る
 * （docs/contract-status-flow.md「契約更新」）。
 */
const ContractAmendModal: React.FC<ContractAmendModalProps> = ({
  thread,
  projectId,
  onClose,
  onSubmit,
  isSubmitting = false,
  errorMessage,
}) => {
  // 契約条件の一部（納品形式・納品方法など）は Detail API の `offer_form` にしか無い。
  const { data: detailData, isLoading: isDetailLoading } =
    useGetDmThreadDetailQuery({ project_id: projectId, dm_id: thread.id });
  const rawOfferForm = detailData?.data.offer_form ?? null;
  const offerForm: SubcontractOfferForm | null = isSubcontractOfferForm(
    rawOfferForm,
    thread.thread_type,
  )
    ? rawOfferForm
    : null;

  const { data: currentUser } = useGetCurrentUserQuery();

  const initial = useMemo<AmendFormState | null>(() => {
    if (isDetailLoading) return null;
    return {
      assignedRoles: thread.assigned_roles ?? "",
      deliverableFormat: offerForm?.deliverable_format ?? "",
      deadline: thread.deadline,
      compensationLines: (offerForm?.compensation_lines ?? []).map((line) => ({
        label: line.label,
        unitPrice: String(line.unit_price ?? "0"),
        quantity: String(line.quantity ?? "1"),
        unit: line.unit ?? "",
      })),
      specialProvisions: offerForm?.special_provisions ?? "",
      scope: thread.scope ?? "",
      initialMessage:
        offerForm?.initial_message ?? thread.initial_message ?? "",
      contractStartDate: thread.contract_start_date,
      contractEndDate: thread.contract_end_date,
      deliveryMethod: offerForm?.delivery_method ?? "",
      deliveryLocation: offerForm?.delivery_location ?? "",
    };
  }, [thread, offerForm, isDetailLoading]);

  const [form, setForm] = useState<AmendFormState | null>(null);
  // Detail の到着後に一度だけ初期値を流し込む（以降はユーザー入力を優先）。
  const current = form ?? initial;

  const patch = (next: Partial<AmendFormState>) => {
    if (!current) return;
    setForm({ ...current, ...next });
  };

  const subtotalExclTax = computeCompensationSubtotalExclusive(
    current?.compensationLines,
  );

  // 増額のときだけ、差分ぶんの残高が足りているかを事前に警告する。
  // 実際に escrow が動くのは受託者の承認時なので、ここでは止めずに注意喚起にとどめる。
  const increaseExclTax = Math.max(
    0,
    subtotalExclTax - (thread.contract_amount ?? 0),
  );
  const balanceCheck = evaluateOfferEscrowBalanceCheck({
    contractAmountExclusive: increaseExclTax,
    userData: currentUser?.data,
  });
  const showBalanceWarning =
    increaseExclTax > 0 && balanceCheck.applicable && !balanceCheck.sufficient;

  /** 初期値から実際に変わったフィールドだけを API 形式で集める。 */
  const changes = useMemo<ContractAmendmentDraft>(() => {
    if (!current || !initial) return {};
    const draft: ContractAmendmentDraft = {};
    if (current.assignedRoles !== initial.assignedRoles) {
      draft.assigned_roles = current.assignedRoles;
    }
    if (current.deliverableFormat !== initial.deliverableFormat) {
      draft.deliverable_format = current.deliverableFormat;
    }
    if (current.deadline !== initial.deadline) draft.deadline = current.deadline;
    if (!sameLines(current.compensationLines, initial.compensationLines)) {
      const lines = mapCompensationLinesToApi(current.compensationLines);
      if (lines) draft.compensation_lines = lines;
    }
    if (current.specialProvisions !== initial.specialProvisions) {
      draft.special_provisions = current.specialProvisions;
    }
    if (current.scope !== initial.scope) draft.scope = current.scope;
    if (current.initialMessage !== initial.initialMessage) {
      draft.initial_message = current.initialMessage;
    }
    if (current.contractStartDate !== initial.contractStartDate) {
      draft.contract_start_date = current.contractStartDate;
    }
    if (current.contractEndDate !== initial.contractEndDate) {
      draft.contract_end_date = current.contractEndDate;
    }
    if (current.deliveryMethod !== initial.deliveryMethod) {
      draft.delivery_method = current.deliveryMethod;
    }
    if (current.deliveryLocation !== initial.deliveryLocation) {
      draft.delivery_location = current.deliveryLocation;
    }
    return draft;
  }, [current, initial]);

  const hasChanges = Object.keys(changes).length > 0;

  return (
    <Modal
      onClose={isSubmitting ? undefined : onClose}
      title="契約書を更新"
      description="変更内容は受託者の承認後に反映されます。"
      size="default"
      primaryAction={{
        label: isSubmitting ? "送信中..." : "更新を提案",
        onClick: () => onSubmit(changes),
        disabled: !hasChanges || isSubmitting,
        rightIcon: "send",
      }}
      secondaryAction={{
        label: "戻る",
        onClick: onClose,
        disabled: isSubmitting,
      }}
    >
      <Notice>
        提案した時点では契約は変わりません。受託者が承認すると新しい契約が有効になり、
        更新前の契約書はスレッドに残ります。変更の理由は、このモーダルを閉じたあと
        DM に書き添えてください。
      </Notice>

      {current && (
        <FormRoot>
          {/* ロール */}
          <FormSection>
            <FormSectionTitle>ロール</FormSectionTitle>
            <FieldRow>
              <Input
                label="ロール名"
                option="required"
                value={current.assignedRoles}
                onChange={(e) => patch({ assignedRoles: e.target.value })}
                placeholder="例: プロデュース"
              />
            </FieldRow>
          </FormSection>

          {/* 条件 */}
          <FormSection>
            <FormSectionTitle>条件</FormSectionTitle>

            <FieldRow>
              <ComboInput
                label="納品形式"
                value={current.deliverableFormat}
                onChange={(e) => patch({ deliverableFormat: e.target.value })}
                options={[]}
                placeholder="納品形式を入力または選択"
              />
            </FieldRow>

            <FieldRow>
              <DateInput
                label="納期"
                option="required"
                value={toDate(current.deadline)}
                onChange={(e) => patch({ deadline: toIso(e.target.value) })}
                options={{ enableTime: true }}
                placeholder="納期を選択"
              />
            </FieldRow>

            <FieldRow>
              <FieldLabel text="対価" option="required" />

              <CompensationLinesEditor
                lines={current.compensationLines}
                onChange={(lines) => patch({ compensationLines: lines })}
              />

              {showBalanceWarning && (
                <BalanceNoticeWarning>
                  増額分（{formatYen(addConsumptionTax(increaseExclTax))}）に対して
                  利用可能残高（
                  {formatYen(addConsumptionTax(balanceCheck.availableExclusive))}
                  ）が不足しています（不足{" "}
                  {formatYen(addConsumptionTax(balanceCheck.shortfallExclusive))}
                  ）。このままでは受託者が承認した時点でエラーになるため、残高を追加するか
                  対価を調整してください。
                </BalanceNoticeWarning>
              )}
            </FieldRow>

            <FieldRow>
              <FieldLabel text="特記事項" option="optional" />
              <Textarea
                rows={3}
                placeholder="特記事項を入力"
                value={current.specialProvisions}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  patch({ specialProvisions: e.target.value })
                }
              />
            </FieldRow>
          </FormSection>

          {/* 内容 */}
          <FormSection>
            <FormSectionTitle>内容</FormSectionTitle>

            <FieldRow>
              <Input
                label="スコープ"
                option="optional"
                value={current.scope}
                onChange={(e) => patch({ scope: e.target.value })}
              />
            </FieldRow>

            <FieldRow>
              <FieldLabel text="メッセージ" option="optional" />
              <Textarea
                rows={7}
                value={current.initialMessage}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  patch({ initialMessage: e.target.value })
                }
              />
            </FieldRow>
          </FormSection>

          {/* 詳細 */}
          <Accordion>
            <AccordionSummary>
              <span>詳細</span>
              <span className="material-symbols-rounded">expand_more</span>
            </AccordionSummary>
            <AccordionBody>
              <FieldRow>
                <FieldLabel text="契約期間" option="optional" />
                <InlineRow>
                  <DateInput
                    value={toDate(current.contractStartDate)}
                    onChange={(e) =>
                      patch({ contractStartDate: toIso(e.target.value) })
                    }
                    options={{ dateFormat: "Y/m/d" }}
                    placeholder="開始日"
                  />
                  <DateInput
                    value={toDate(current.contractEndDate)}
                    onChange={(e) =>
                      patch({ contractEndDate: toIso(e.target.value) })
                    }
                    options={{ dateFormat: "Y/m/d" }}
                    placeholder="終了日"
                  />
                </InlineRow>
              </FieldRow>

              <FieldRow>
                <Input
                  label="取引方法"
                  option="optional"
                  value={current.deliveryMethod}
                  onChange={(e) => patch({ deliveryMethod: e.target.value })}
                />
              </FieldRow>

              <FieldRow>
                <Input
                  label="引渡場所"
                  option="optional"
                  value={current.deliveryLocation}
                  onChange={(e) => patch({ deliveryLocation: e.target.value })}
                />
              </FieldRow>
            </AccordionBody>
          </Accordion>
        </FormRoot>
      )}

      {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
    </Modal>
  );
};

export default ContractAmendModal;
